import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Save, Plus, Trash2 } from "lucide-react";
import { CATEGORY_LABELS } from "./PricingDialog";

const newKey = () => Math.random().toString(36).slice(2, 10);
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export const QuoteDialog = ({ booking, pricing, onClose, onUpdated }) => {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking || !pricing) return;
    const existing = booking.quote;
    const pkg = pricing.packages[0];
    const cat = Object.keys(CATEGORY_LABELS)[0];
    setQ(existing ? { ...existing, lab_addons: (existing.lab_addons || []).map((a) => ({ ...a, _key: a._key || newKey() })) } : {
      package_key: pkg.key, package_label: pkg.label, package_price_per_person: pkg.price_per_person,
      participants: booking.num_participants, free_amount: 0, free_description: "", notes: "",
      lab_addons: (booking.selected_labs || []).map((l) => ({ _key: newKey(), category: cat, lab_title: l.lab_title, date: l.date || booking.check_in, slot: "mattino",
        participants: l.participants, price_per_person: pricing.lab_categories[cat] ?? 0 })),
    });
  }, [booking, pricing]);

  const total = useMemo(() => {
    if (!q) return 0;
    const base = q.package_key === "free" ? Number(q.free_amount || 0) : Number(q.participants || 0) * Number(q.package_price_per_person || 0);
    return base + q.lab_addons.reduce((a, l) => a + Number(l.participants || 0) * Number(l.price_per_person || 0), 0);
  }, [q]);

  if (!booking || !q) return null;

  const choosePackage = (key) => {
    if (key === "free") return setQ({ ...q, package_key: "free", package_label: "Tariffa libera", package_price_per_person: 0 });
    const p = pricing.packages.find((x) => x.key === key);
    setQ({ ...q, package_key: p.key, package_label: p.label, package_price_per_person: p.price_per_person });
  };
  const addAddon = (lab) => {
    const cat = Object.keys(CATEGORY_LABELS)[0];
    setQ({ ...q, lab_addons: [...q.lab_addons, { _key: newKey(), category: cat, lab_title: lab?.lab_title || "", date: lab?.date || booking.check_in, slot: "mattino",
      participants: lab?.participants || q.participants, price_per_person: pricing.lab_categories[cat] ?? 0 }] });
  };
  const setAddon = (i, field, value) => {
    const lab_addons = q.lab_addons.map((a, ix) => {
      if (ix !== i) return a;
      const next = { ...a, [field]: value };
      if (field === "category") next.price_per_person = pricing.lab_categories[value] ?? 0;
      return next;
    });
    setQ({ ...q, lab_addons });
  };
  const removeAddon = (i) => setQ({ ...q, lab_addons: q.lab_addons.filter((_, ix) => ix !== i) });

  const payload = () => ({ ...q, participants: Number(q.participants), free_amount: Number(q.free_amount || 0), package_price_per_person: Number(q.package_price_per_person || 0),
    lab_addons: q.lab_addons.map(({ _key, ...a }) => ({ ...a, participants: Number(a.participants), price_per_person: Number(a.price_per_person) })), total });

  const save = async (thenSend = false) => {
    setBusy(true);
    try {
      let { data } = await api.put(`/bookings/${booking.id}/quote`, payload());
      if (thenSend) {
        ({ data } = await api.post(`/bookings/${booking.id}/quote/send`));
        toast.success(`Preventivo inviato a ${booking.contact_email}`);
      } else toast.success("Preventivo salvato");
      onUpdated(data);
      if (thenSend) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">Preventivo · {booking.reference}</DialogTitle></DialogHeader>
        <div className="text-sm text-[#6B6960] -mt-2">
          {booking.organization || booking.group_type} · {booking.contact_first_name} {booking.contact_last_name} · {booking.contact_email} · {booking.check_in} → {booking.check_out}
          {booking.selected_labs?.length > 0 && <div className="mt-1">Laboratori richiesti: {booking.selected_labs.map((l) => l.lab_title).join(", ")}</div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Pacchetto</Label>
            <Select value={q.package_key} onValueChange={choosePackage}>
              <SelectTrigger data-testid="quote-package-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {pricing.packages.map((p) => <SelectItem key={p.key} value={p.key}>{p.label} · {eur(p.price_per_person)}/pax</SelectItem>)}
                <SelectItem value="free">Tariffa libera</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {q.package_key !== "free" ? (
            <>
              <div><Label>Partecipanti</Label><Input type="number" min="1" value={q.participants} onChange={(e) => setQ({ ...q, participants: e.target.value })} data-testid="quote-participants" /></div>
              <div><Label>Prezzo a persona (€)</Label><Input type="number" min="0" step="0.5" value={q.package_price_per_person} onChange={(e) => setQ({ ...q, package_price_per_person: e.target.value })} data-testid="quote-pkg-price" /></div>
            </>
          ) : (
            <>
              <div><Label>Importo totale (€)</Label><Input type="number" min="0" step="0.5" value={q.free_amount} onChange={(e) => setQ({ ...q, free_amount: e.target.value })} data-testid="quote-free-amount" /></div>
              <div className="md:col-span-2"><Label>Descrizione</Label><Input value={q.free_description || ""} onChange={(e) => setQ({ ...q, free_description: e.target.value })} data-testid="quote-free-desc" /></div>
            </>
          )}
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="overline">Laboratori · giorno e fascia (Mattino / Pomeriggio)</div>
            <Button size="sm" variant="outline" onClick={() => addAddon()} className="rounded-full" data-testid="quote-add-lab"><Plus size={14} className="mr-1" /> Aggiungi</Button>
          </div>
          <p className="text-xs text-[#6B6960] mt-1">Giorno e fascia vengono riportati nel programma e negli eventi di Google Calendar alla conferma.</p>
          {q.lab_addons.length === 0 && <div className="text-sm text-[#6B6960] mt-2">Nessun laboratorio aggiunto.</div>}
          {q.lab_addons.map((a, i) => (
            <div key={a._key} className="grid grid-cols-12 gap-2 items-end mt-3 border-b border-[#E2DFD6] pb-3" data-testid={`quote-lab-${i}`}>
              <div className="col-span-12 md:col-span-3"><Label>Laboratorio</Label><Input value={a.lab_title || ""} onChange={(e) => setAddon(i, "lab_title", e.target.value)} placeholder="es. Litografia" data-testid={`quote-lab-title-${i}`} /></div>
              <div className="col-span-6 md:col-span-2"><Label>Tipologia</Label>
                <Select value={a.category} onValueChange={(v) => setAddon(i, "category", v)}>
                  <SelectTrigger data-testid={`quote-lab-category-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CATEGORY_LABELS).map((k) => <SelectItem key={k} value={k}>{CATEGORY_LABELS[k]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-6 md:col-span-2"><Label>Giorno</Label><Input type="date" min={booking.check_in} max={booking.check_out} value={a.date || ""} onChange={(e) => setAddon(i, "date", e.target.value)} data-testid={`quote-lab-date-${i}`} /></div>
              <div className="col-span-6 md:col-span-2"><Label>Fascia</Label>
                <Select value={a.slot || "mattino"} onValueChange={(v) => setAddon(i, "slot", v)}>
                  <SelectTrigger data-testid={`quote-lab-slot-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="mattino">Mattino (9:30–12:30)</SelectItem><SelectItem value="pomeriggio">Pomeriggio (15:00–18:00)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-3 md:col-span-1"><Label>Pax</Label><Input type="number" min="1" value={a.participants} onChange={(e) => setAddon(i, "participants", e.target.value)} /></div>
              <div className="col-span-3 md:col-span-1"><Label>€/pax</Label><Input type="number" min="0" step="0.5" value={a.price_per_person} onChange={(e) => setAddon(i, "price_per_person", e.target.value)} /></div>
              <button type="button" onClick={() => removeAddon(i)} className="col-span-1 h-10 flex items-center justify-center text-[#8C3A27]" data-testid={`quote-lab-remove-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>

        <div className="mt-6"><Label>Note per il cliente (opzionale)</Label><Textarea rows={3} value={q.notes || ""} onChange={(e) => setQ({ ...q, notes: e.target.value })} data-testid="quote-notes" /></div>

        <div className="mt-6 flex items-center justify-between border-t border-[#E2DFD6] pt-4">
          <div><div className="overline">Totale preventivo</div><div className="font-display text-3xl text-[#8C3A27]" data-testid="quote-total">{eur(total)}</div></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save(false)} disabled={busy} className="rounded-full" data-testid="quote-save"><Save size={16} className="mr-2" /> Salva</Button>
            <Button onClick={() => save(true)} disabled={busy} className="btn-primary-cdm rounded-full" data-testid="quote-send"><Send size={16} className="mr-2" /> {busy ? "Invio…" : "Invia preventivo"}</Button>
          </div>
        </div>
        {q.sent_at && <div className="text-xs text-[#6B6960] mt-2">Ultimo invio: {new Date(q.sent_at).toLocaleString("it-IT")}</div>}
      </DialogContent>
    </Dialog>
  );
};
